import { useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setUser, userLoggedOut } from "store/auth/userSlice";
import { onSignInSuccess, onSignOutSuccess } from "store/auth/sessionSlice";
import {
  apiSignIn,
  apiSignOut,
  apiSignInGoogle,
  apiSignInGithub,
} from "services/AuthService";
import appConfig from "configs/app.config";
import { PERSIST_STORE_NAME, REDIRECT_URL_KEY } from "constants/app.constant";
import { useNavigate, useSearchParams } from "react-router-dom";
import useQuery from "./useQuery";
import { reset } from "store/studio/slice";
import { reset as propertyReset } from "store/studio/propertiesSlice";
import deepParseJson from "utils/deepParseJson";
import { logger } from "utils/logger";
import { isExact } from "utils/isEqual";
import { useToastHandler } from "utils/hooks/useErrorHandler";
import { Environment, getEnvironment } from "utils/getEnvironment";

const websiteUrl = getEnvironment(Environment.DEVELOPMENT)
  ? "https://d3nez87f1dbwih.cloudfront.net/playground": import.meta.env.VITE_APP_ENV  === "staging" ? "https://d3nez87f1dbwih.cloudfront.net/playground"
  : "https://autogon.ai/playground";

function useAuth() {
  const dispatch = useDispatch();
  const [successHandler, errorHandler] = useToastHandler();
  const { userName } = useSelector((state) => state.auth.user);

  const [searchParams] = useSearchParams();
  const playgroundAuth = searchParams.get("authorization");

  const navigate = useNavigate();

  const query = useQuery();

  const { token, signedIn, refresh } = useSelector(
    (state) => state.auth.session
  );

  const signIn = useCallback(
    async ({ email, password }) => {
      let anonymous = {
        avatar: "",
        email: "Anonymous",
        authority: ["USER"],
        userName: "",
      };

      const route = playgroundAuth
        ? "/verify?authorization=playground"
        : appConfig.verificationEntryPath;

      try {
        const resp = await apiSignIn({ email, password });

        // All errors happen here
        if (resp.error) {
          errorHandler("Sign in", resp.error);

          // Unverified user account and check if coming from playground
          if (resp?.error?.data?.verified === false) {
            navigate(route, { state: { email } });
            return;
          }

          return;
        }

        const { token, userDetails } = resp.data;

        if (!userDetails) {
          errorHandler(
            "Email auth",
            resp.data?.message ??
              "Unknown error occurred, try again (user detail)"
          );

          if (
            resp.data.message ===
            "To continue login, check your inbox for an OTP we sent you."
          ) {
            navigate(route, { state: { email } });
          }

          return;
        }

        // Redirect back to playground
        if (playgroundAuth === "playground" && token?.access) {
          window.location.assign(`${websiteUrl}?token=${token?.access}`);
          return;
        }

        dispatch(onSignInSuccess(token));

        const { first_name, last_name, ...user } = userDetails;

        const name = `${first_name} ${last_name}`;

        if (!isExact(userName, name)) {
          dispatch(reset());
          dispatch(propertyReset());
        }

        dispatch(
          setUser(
            {
              ...user,
              authority: ["User"],
              userName: name,
              avatar:
                resp?.data?.userDetails?.picture_url ??
                "/img/avatars/thumb-1.jpg",
            } || anonymous
          )
        );

        const redirectUrl = query.get(REDIRECT_URL_KEY);
        navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath);
      } catch (errors) {
        console.log("err", errors);
      }
    },
    [dispatch, navigate, query]
  );

  const onGithubAuth = useCallback(async ({ code }) => {
    try {
      const response = await apiSignInGithub({ code });

      if (response.error)
        return errorHandler("Github auth", response.error.data.detail);

      // Unverified user account and check if coming from playground
      if (response.error && response?.error?.data?.verified === false) {
        errorHandler("Sign in", resp.error);
        navigate(
          playgroundAuth
            ? "/verify?authorization=playground"
            : appConfig.verificationEntryPath,
          { state: { email } }
        );
        return;
      }

      const { token, userDetails } = response.data;

      // Redirect back to playground
      if (playgroundAuth === "playground") {
        window.location.assign(`${websiteUrl}?token=${token?.access}`);
        return;
      }

      if (!userDetails) {
        errorHandler(
          "Github auth",
          "Unknown error occurred, try again (user detail)"
        );
        return;
      }

      dispatch(onSignInSuccess(token));

      const { first_name, last_name, ...user } = userDetails;

      const name = `${first_name} ${last_name}`;

      if (!isExact(userName, name)) {
        dispatch(reset());
        dispatch(propertyReset());
      }

      dispatch(
        setUser(
          {
            ...user,
            authority: ["User"],
            userName: name,
            avatar: user?.picture_url ?? "/img/avatars/thumb-1.jpg",
          } || anonymous
        )
      );

      const redirectUrl = query.get(REDIRECT_URL_KEY);
      navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath);
    } catch (error) {
      errorHandler("Github auth", error);
    }
  }, []);

  const onGoogleAuth = useCallback(async ({ auth_token }) => {
    try {
      const response = await apiSignInGoogle({ auth_token });

      if (response.error)
        return errorHandler("Google auth", response.error.data.message);

      // Unverified user account and check if coming from playground
      if (response.error && response?.error?.data?.verified === false) {
        errorHandler("Sign in", resp.error);
        navigate(
          playgroundAuth
            ? "/verify?authorization=playground"
            : appConfig.verificationEntryPath,
          { state: { email } }
        );
        return;
      }

      const { token, userDetails } = response.data;

      // Redirect back to playground
      if (playgroundAuth === "playground") {
        window.location.assign(`${websiteUrl}?token=${token?.access}`);
        return;
      }

      if (!userDetails) {
        errorHandler(
          "Google auth",
          "Unknown error occurred, try again (user detail)"
        );
        return;
      }

      dispatch(onSignInSuccess(token));

      const { first_name, last_name, ...user } = userDetails;

      const name = `${first_name} ${last_name}`;

      if (!isExact(userName, name)) {
        dispatch(reset());
        dispatch(propertyReset());
      }

      dispatch(
        setUser(
          {
            ...user,
            userName: name,
            authority: ["User"],
            avatar: user?.picture_url ?? "/img/avatars/thumb-1.jpg",
          } || anonymous
        )
      );

      const redirectUrl = query.get(REDIRECT_URL_KEY);
      navigate(redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath);
    } catch (error) {
      errorHandler("Google auth", error);
    }
  }, []);

  const handleSignOut = () => {
    dispatch(onSignOutSuccess());

    dispatch(userLoggedOut());
    dispatch(reset());
    dispatch(propertyReset());

    navigate(appConfig.unAuthenticatedEntryPath);
  };

  const signOut = async () => {
    const localItem = deepParseJson(localStorage.getItem(PERSIST_STORE_NAME));
    const refresh = localItem?.auth.session.refresh;

    try {
      await apiSignOut({ refresh });
    } catch (errors) {
      logger(errors);
    }
    handleSignOut();
  };

  return {
    authenticated: token && signedIn,
    signIn,
    signOut,
    onGithubAuth,
    onGoogleAuth,
  };
}

export default useAuth;
